import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { mutate } from "swr";

import { deleteEntry, mergeEntries } from "../../../api";
import useCompetition, {
  useCompetitionEntries,
} from "../../../hooks/useCompetition";
import useDebounce from "../../../hooks/useDebounce";
import useErrorAlert from "../../../hooks/useErrorAlert";
import useLocalStorage from "../../../hooks/useLocalStorage";
import getGenericErrorInfo from "../../../utils/getGenericErrorInfo";
import getCanSeeLeaderboard from "../../../utils/getCanSeeLeaderboard";

export default function useEntriesScreen() {
  const { competitionUUID } = useParams();
  const { competition } = useCompetition(competitionUUID);
  const { entries } = useCompetitionEntries(competitionUUID);
  const [selectedCategoryUUIDs, setSelectedCategoryUUIDs] = useLocalStorage<
    string[]
  >(`${competitionUUID}/selectedCategories`, []);

  const hasSetCategories = useRef(false);

  useEffect(() => {
    if (
      competition?.userEntry &&
      !selectedCategoryUUIDs.length &&
      !hasSetCategories.current
    ) {
      setSelectedCategoryUUIDs([competition.userEntry.category]);
      hasSetCategories.current = true;
    }
  }, [competition?.userEntry, selectedCategoryUUIDs, setSelectedCategoryUUIDs]);

  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);

  const canViewLeaderboard = competition
    ? getCanSeeLeaderboard(competition)
    : undefined;
  const isStaff =
    competition && ["MASTER", "STAFF_JUDGE"].includes(competition?.role || "");

  const canModifyEntries =
    isStaff ||
    (competition?.role === "JUDGE" &&
      competition?.settings.judgeCompetitorPermissions === "ADD_MODIFY");
  const canAddEntries =
    isStaff ||
    (competition?.role === "JUDGE" &&
      ["ADD", "ADD_MODIFY"].includes(
        competition?.settings.judgeCompetitorPermissions
      ));

  const selectedCategories = useMemo(
    () =>
      competition?.stages[0]?.categories.filter((cat) =>
        selectedCategoryUUIDs.includes(cat.uuid)
      ) || [],
    [selectedCategoryUUIDs, competition]
  );

  const [searchText, setSearchText] = useState("");
  const debouncedSearchText = useDebounce(searchText, 100);

  const filteredEntries = entries?.filter((entry) => {
    const matchesCategory = selectedCategoryUUIDs.length
      ? selectedCategoryUUIDs.includes(entry.category?.uuid || "")
      : true;
    return (
      matchesCategory &&
      (entry.user.firstName
        .toLowerCase()
        .startsWith(debouncedSearchText.toLowerCase()) ||
        entry.user.lastName
          .toLowerCase()
          .startsWith(debouncedSearchText.toLowerCase()) ||
        (entry.user.firstName + " " + entry.user.lastName)
          .toLowerCase()
          .startsWith(debouncedSearchText.toLowerCase()))
    );
  });

  const [selectedEntryUUID, setSelectedEntryUUID] = useState<string | null>(
    null
  );
  const selectedEntry = useMemo(
    () => entries?.find((x) => x.user.uuid === selectedEntryUUID),
    [entries, selectedEntryUUID]
  );

  const onDeleteSelectedEntry = () => {
    if (selectedEntry?.attemptsCount || 0 > 0) {
      if (
        window.confirm(
          `This entry has ${selectedEntry?.attemptsCount} attempts logged. Are you sure you want to delete this entry and all its attempts?`
        )
      ) {
        onConfirmDelete();
      }
    }
  };

  const [isMerging, setIsMerging] = useState(false);
  const [mergeTargetUUID, setMergeTargetUUID] = useState<string | null>(null);
  const mergeTarget = useMemo(
    () => entries?.find((x) => x.user.uuid === mergeTargetUUID),
    [entries, mergeTargetUUID]
  );

  const onCancelMerge = () => {
    setIsMerging(false);
    setMergeTargetUUID(null);
  };

  const onEntryClick = (uuid: string) => {
    if (canModifyEntries) {
      if (uuid === selectedEntryUUID) {
        return;
      }
      if (isMerging) {
        setMergeTargetUUID(uuid);
        setIsBottomSheetOpen(true);
      } else {
        setSelectedEntryUUID(uuid);
      }
    } else {
      setIsScorecardModalOpen(true);
    }
  };

  const [isMutateInProgress, setIsMutateInProgress] = useState(false);

  const { setError } = useErrorAlert();

  const onConfirmMerge = async () => {
    if (!competitionUUID || !selectedEntryUUID || !mergeTargetUUID) {
      return;
    }
    try {
      setIsMutateInProgress(true);
      await mergeEntries({
        competitionUUID,
        entryUUID: selectedEntryUUID,
        targetEntryUUID: mergeTargetUUID,
      });
      setIsMerging(false);
      setIsBottomSheetOpen(false);
      setMergeTargetUUID(null);
      setIsMutateInProgress(false);
      mutate(`/competitions/${competitionUUID}/entries`);
    } catch (error) {
      setError(getGenericErrorInfo(error));
      setIsMutateInProgress(false);
    }
  };

  const onConfirmDelete = async () => {
    if (!competitionUUID || !selectedEntryUUID) {
      return;
    }
    try {
      setIsMutateInProgress(true);
      await deleteEntry({
        competitionUUID,
        entryUUID: selectedEntryUUID,
      });
      setSelectedEntryUUID(null);
      setIsBottomSheetOpen(false);
      mutate(`/competitions/${competitionUUID}/entries`);
      setIsMutateInProgress(false);
    } catch (error) {
      setError(getGenericErrorInfo(error));
      setIsMutateInProgress(false);
      setIsBottomSheetOpen(false);
    }
  };

  const navigate = useNavigate();
  const onModifyEntryClick = (entryUUID: string) => navigate(`./${entryUUID}`);

  const [isScorecardModalOpen, setIsScorecardModalOpen] = useState(false);

  return {
    canViewLeaderboard,
    canAddEntries,
    canModifyEntries,
    competition,
    entries: filteredEntries,
    selectedCategories,
    setSelectedCategoryUUIDs,
    selectedEntryUUID,
    selectedEntry,
    setSelectedEntryUUID,
    isBottomSheetOpen,
    setIsBottomSheetOpen,
    onDeleteSelectedEntry,
    setIsMerging,
    isMerging,
    onEntryClick,
    mergeTargetUUID,
    mergeTarget,
    setMergeTargetUUID,
    onCancelMerge,
    onConfirmMerge,
    searchText,
    setSearchText,
    isMutateInProgress,
    onModifyEntryClick,
    isScorecardModalOpen,
    setIsScorecardModalOpen,
  };
}
